import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Tree, Donation } from '../types/tree';
import { treeMocks } from '../mocks/treeMocks';

interface TreeContextType {
  trees: Tree[];
  currentTree: Tree | null;
  getTreeById: (id: string) => Tree | undefined;
  setCurrentTree: (tree: Tree | null) => void;
  addDonation: (
    treeId: string,
    donation: Omit<Donation, 'id' | 'donatedAt'>
  ) => void;
  updateTreeProgress: (treeId: string, newProgress: number) => void;
}

const TreeContext = createContext<TreeContextType | undefined>(undefined);

interface TreeProviderProps {
  children: ReactNode;
}

export const TreeProvider: React.FC<TreeProviderProps> = ({ children }) => {
  const [trees, setTrees] = useState<Tree[]>(treeMocks);
  const [currentTree, setCurrentTree] = useState<Tree | null>(null);

  const getTreeById = (id: string): Tree | undefined => {
    return trees.find(tree => tree.id === id);
  };

  const addDonation = (
    treeId: string,
    donation: Omit<Donation, 'id' | 'donatedAt'>
  ) => {
    setTrees(prevTrees =>
      prevTrees.map(tree => {
        if (tree.id === treeId) {
          const newDonation: Donation = {
            ...donation,
            id: `d${Date.now()}`,
            donatedAt: new Date().toISOString(),
          };

          const newTotalDonations = tree.totalDonations + donation.amount;
          const newDonorsCount = tree.donations.length + 1;

          return {
            ...tree,
            totalDonations: newTotalDonations,
            donorsCount: newDonorsCount,
            donations: [...tree.donations, newDonation],
            updatedAt: new Date().toISOString(),
          };
        }
        return tree;
      })
    );
  };

  const updateTreeProgress = (treeId: string, newProgress: number) => {
    setTrees(prevTrees =>
      prevTrees.map(tree => {
        if (tree.id === treeId) {
          return {
            ...tree,
            totalDonations: newProgress,
            updatedAt: new Date().toISOString(),
          };
        }
        return tree;
      })
    );
  };

  const value: TreeContextType = {
    trees,
    currentTree,
    getTreeById,
    setCurrentTree,
    addDonation,
    updateTreeProgress,
  };

  return <TreeContext.Provider value={value}>{children}</TreeContext.Provider>;
};

export const useTree = (): TreeContextType => {
  const context = useContext(TreeContext);
  if (context === undefined) {
    throw new Error('useTree must be used within a TreeProvider');
  }
  return context;
};
